import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck, ShieldAlert, Upload, Loader2, Clock, History } from "lucide-react";

const TONE = {
  verified: "bg-emerald-50 text-emerald-600",
  extended: "bg-indigo-50 text-indigo-600",
  awaiting_review: "bg-amber-50 text-amber-600",
  waived: "bg-sky-50 text-sky-600",
  missing: "bg-slate-100 text-slate-500",
  rejected: "bg-red-50 text-red-600",
  expired: "bg-red-50 text-red-600",
};
const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30";

export const CredentialCenter = ({ role, title = "Credential Center" }) => {
  const [data, setData] = useState(undefined);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ expires_at: "", state: "", number: "", file: null });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/credentials/me?role=${role}`).then(({ data: d }) => setData(d)).catch(() => setData(null));
  }, [role]);
  useEffect(() => { load(); }, [load]);

  const upload = async (credential_type) => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("role", role); fd.append("credential_type", credential_type);
      fd.append("expires_at", form.expires_at); fd.append("state", form.state); fd.append("number", form.number);
      if (form.file) fd.append("file", form.file);
      await api.post("/credentials/upload", fd);
      toast.success("Credential submitted");
      setOpen(null); setForm({ expires_at: "", state: "", number: "", file: null }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setSaving(false); }
  };

  if (data === undefined) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  if (data === null) return <div className="py-10 text-center text-sm text-slate-400" data-testid="credential-center-error">Could not load credentials.</div>;

  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid={`credential-center-${role}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cobalt" /> {title}</h3>
          <p className="text-xs text-slate-400 mt-0.5">Documents are stored in protected private storage. An expired credential pauses <b>new assignments only</b> — your account, history and existing cases are never affected.</p>
        </div>
        <span data-testid="credential-overall-status" className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full ${data.overall === "compliant" ? "bg-emerald-50 text-emerald-600" : data.overall === "at_risk" ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}`}>
          {data.overall.replace("_", " ")}
        </span>
      </div>

      {(data.blockers || []).length > 0 && (
        <div className="mt-3 rounded-2xl bg-red-50 border border-red-200 p-3 text-sm text-red-600" data-testid="credential-blockers">
          <div className="font-semibold flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> New assignments paused</div>
          {data.blockers.map((b, i) => <div key={i} className="text-xs mt-1">• {b.label} is {b.status}{b.blocks ? ` — blocks ${b.blocks.replace(/_/g, " ")}` : ""}</div>)}
        </div>
      )}

      {(data.items || []).length === 0 ? (
        <div className="mt-4 py-8 text-center text-sm text-slate-400" data-testid="credential-empty">No credential requirements are currently configured for this role.</div>
      ) : (
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          {data.items.map((it) => (
            <div key={it.credential_type} className="rounded-2xl border border-slate-100 p-4" data-testid={`credential-item-${it.credential_type}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-medium text-navy">{it.label} {it.required && <span className="text-[9px] font-bold uppercase text-slate-400 ml-1">required</span>}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {it.expires_at ? `Expires ${it.expires_at}` : "No expiration on file"}
                    {it.days_left != null && it.days_left >= 0 && it.days_left <= 30 && <span className="text-amber-600 font-semibold ml-1"><Clock className="w-3 h-3 inline" /> {it.days_left}d left</span>}
                    {it.days_left != null && it.days_left < 0 && <span className="text-red-500 font-semibold ml-1">expired</span>}
                  </div>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full whitespace-nowrap ${TONE[it.status] || TONE.missing}`} data-testid={`credential-status-${it.credential_type}`}>
                  {it.status.replace(/_/g, " ")}
                </span>
              </div>
              {open === it.credential_type ? (
                <div className="mt-3 space-y-2" data-testid={`credential-form-${it.credential_type}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Expiration date</label>
                      <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid={`credential-expires-${it.credential_type}`} className={inputCls} /></div>
                    <div><label className="text-[10px] font-semibold text-slate-500 uppercase">State (optional)</label>
                      <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} data-testid={`credential-state-${it.credential_type}`} className={inputCls} placeholder="TX" maxLength={2} /></div>
                  </div>
                  <input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} data-testid={`credential-number-${it.credential_type}`} className={inputCls} placeholder="License / policy number (optional — only last 4 stored)" />
                  <input type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid={`credential-file-${it.credential_type}`} className="text-xs" />
                  <div className="flex gap-2">
                    <button onClick={() => upload(it.credential_type)} disabled={saving} data-testid={`credential-submit-${it.credential_type}`}
                      className="px-4 py-2 rounded-full bg-navy text-white text-xs font-semibold disabled:opacity-50">{saving ? "Submitting…" : "Submit"}</button>
                    <button onClick={() => setOpen(null)} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-500 text-xs">Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setOpen(it.credential_type); setForm({ expires_at: "", state: "", number: "", file: null }); }}
                  data-testid={`credential-upload-${it.credential_type}`}
                  className="mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold hover:bg-slate-200">
                  <Upload className="w-3.5 h-3.5" /> {it.status === "missing" ? "Upload" : "Renew / replace"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {(data.history || []).length > 0 && (
        <details className="mt-4">
          <summary className="text-xs font-semibold text-slate-500 cursor-pointer flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Recent credential activity</summary>
          <div className="mt-2 divide-y divide-slate-50" data-testid="credential-history">
            {data.history.slice(0, 10).map((h) => (
              <div key={h.audit_id} className="py-1.5 text-[11px] flex justify-between gap-3">
                <span className="text-navy font-medium">{h.event}</span>
                <span className="text-slate-400">{new Date(h.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default CredentialCenter;
